import React, { useState } from "react";
import Heading from "carbon-react/lib/components/heading";
import ErrorMessage from "../../messages/ErrorMessage";
import Dialog from "carbon-react/lib/components/dialog";
import {
  StepSequence,
  StepSequenceItem,
} from "carbon-react/lib/components/step-sequence";
import EnterEmailStep from "./EnterEmailStep";
import EnterCodeStep from "./EnterCodeStep";
import EmailChangedStep from "./EmailChangedStep";
import { useAppContext } from "../../../contexts/AppContext";
import { useUserApi } from "../../../services/UserApi";
import ConfirmDialog from "../../common/confirmDialog/ConfirmDialog";

import "./ChangeEmailDialog.css";

const steps = Object.freeze({
  enterEmail: "enterEmail",
  enterCode: "enterCode",
  emailChanged: "emailChanged",
});

const getInitialEnterEmailState = () => {
  return {
    email: "",
    emailValid: true,
    emailValidationMsg: "",
    confirmEmail: "",
    confirmEmailValid: true,
    confirmEmailValidationMsg: "",
    languageCode: null,
    languageCodeValid: true,
    agentComments: "",
    agentCommentsValid: true,
  };
};

const getInitialEnterCodeState = () => {
  return {
    verificationCode: "",
    verificationCodeValid: true,
  };
};

const ChangeEmailDialog = ({ open, onClose, onSave, user }) => {
  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(steps.enterEmail);
  const [enterEmailState, setEnterEmailState] = useState(
    getInitialEnterEmailState()
  );
  const [enterCodeState, setEnterCodeState] = useState(
    getInitialEnterCodeState()
  );
  const [busy, setBusy] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const { toastSuccess } = useAppContext();
  const { startChangeEmail, completeChangeEmail } = useUserApi();

  const onConfirmCloseDialogCancel = () => {
    setConfirmCloseDialogOpen(false);
  };

  const onConfirmCloseDialogAccept = () => {
    setConfirmCloseDialogOpen(false);
    onDialogClose();
  };

  const showDialogError = (msg) => {
    const sanistizedMsg =
      msg && msg.length > 0 ? msg.trim() : "Operation failed";
    setErrorMsg(sanistizedMsg);
    setShowError(true);
  };

  const clearDialogError = () => {
    setErrorMsg("");
    setShowError(false);
  };

  const onDialogClose = () => {
    onClose();
    resetState();
  };

  const resetState = () => {
    setConfirmCloseDialogOpen(false);
    clearDialogError();
    setEnterEmailState(getInitialEnterEmailState());
    setEnterCodeState(getInitialEnterCodeState());
    setCurrentStep(steps.enterEmail);
  };

  const onDialogCancel = () => {
    if (busy) return;
    if (unsavedDataPresent()) {
      setConfirmCloseDialogOpen(true);
      return;
    }

    onDialogClose();
  };

  const unsavedDataPresent = () => {
    return (
      (currentStep === steps.enterEmail &&
        (stringHasValue(enterEmailState.email) ||
          stringHasValue(enterEmailState.confirmEmail) ||
          stringHasValue(enterEmailState.languageCode) ||
          stringHasValue(enterEmailState.agentComments))) ||
      currentStep === steps.enterCode
    );
  };

  const stringHasValue = (value) => {
    return value && value.length > 0;
  };

  const onEnterEmailStepCompleted = async () => {
    try {
      setBusy(true);
      clearDialogError();

      const apiResult = await startChangeEmail(
        user.userId,
        enterEmailState.email,
        enterEmailState.languageCode,
        enterEmailState.agentComments
      );

      if (!apiResult?.success)
        throw new Error(
          apiResult?.error?.description || "Failed to request email change"
        );

      toastSuccess(
        `Email change requested. Verification code sent to ${enterEmailState.email}`
      );

      setCurrentStep(steps.enterCode);
    } catch (error) {
      showDialogError(error?.toString());
    }
    setBusy(false);
  };

  const onEnterCodeStepBack = () => {
    clearDialogError();
    setEnterCodeState(getInitialEnterCodeState());
    setCurrentStep(steps.enterEmail);
  };

  const onEnterCodeStepCompleted = async () => {
    try {
      setBusy(true);
      clearDialogError();

      const apiResult = await completeChangeEmail(
        user.userId,
        enterCodeState.verificationCode
      );

      if (!apiResult?.success)
        throw new Error(
          apiResult?.error?.description || "Failed to complete email change"
        );

      toastSuccess(
        `Email change completed. Confirmation sent to ${enterEmailState.email}`
      );

      const updatedUser = apiResult?.data;
      if (updatedUser && onSave) {
        onSave(updatedUser);
      }

      setCurrentStep(steps.emailChanged);
    } catch (error) {
      showDialogError(error?.toString());
    }
    setBusy(false);
  };

  const onEmailChangedStepCompleted = () => {
    onDialogClose();
  };

  const heading = <Heading title="Change email address" />;

  const stepSequence = (
    <StepSequence
      className="change-email-step-sequence"
      orientation="horizontal"
    >
      <StepSequenceItem
        aria-label="Step 1 of 3"
        hiddenCompleteLabel="Complete"
        hiddenCurrentLabel="Current"
        indicator="1"
        status={currentStep === steps.enterEmail ? "current" : "complete"}
      >
        Enter new email address and send code
      </StepSequenceItem>
      <StepSequenceItem
        aria-label="Step 2 of 3"
        hiddenCompleteLabel="Complete"
        hiddenCurrentLabel="Current"
        indicator="2"
        status={
          currentStep === steps.enterEmail
            ? "incomplete"
            : currentStep === steps.enterCode
            ? "current"
            : "complete"
        }
      >
        Enter code
      </StepSequenceItem>
      <StepSequenceItem
        aria-label="Step 3 of 3"
        hiddenCompleteLabel="Complete"
        hiddenCurrentLabel="Current"
        indicator="3"
        status={currentStep === steps.emailChanged ? "complete" : "incomplete"}
      >
        Email address changed
      </StepSequenceItem>
    </StepSequence>
  );

  const enterEmailStep = (
    <EnterEmailStep
      user={user}
      busy={busy}
      state={enterEmailState}
      onStateChanged={setEnterEmailState}
      onCancel={onDialogCancel}
      onCompleted={onEnterEmailStepCompleted}
    />
  );

  const enterCodeStep = (
    <EnterCodeStep
      user={user}
      newEmail={enterEmailState.email}
      busy={busy}
      state={enterCodeState}
      onStateChanged={setEnterCodeState}
      onCancel={onDialogCancel}
      onCompleted={onEnterCodeStepCompleted}
      onBack={onEnterCodeStepBack}
    />
  );

  const emailChangedStep = (
    <EmailChangedStep
      newEmail={enterEmailState.email}
      busy={busy}
      onCompleted={onEmailChangedStepCompleted}
    />
  );

  const stepBody = (
    <>
      {currentStep === steps.enterEmail
        ? enterEmailStep
        : currentStep === steps.enterCode
        ? enterCodeStep
        : emailChangedStep}
    </>
  );

  const errorMessage = (
    <>
      {showError !== true ? null : (
        <ErrorMessage
          message={
            errorMsg && errorMsg.length > 0 ? errorMsg : "The operation failed"
          }
        />
      )}
    </>
  );

  return (
    <>
      {!user ? null : (
        <>
          <Dialog open={open} showCloseIcon={true} onCancel={onDialogCancel}>
            {heading}
            {stepSequence}
            {stepBody}
            {errorMessage}
          </Dialog>
          <ConfirmDialog
            open={confirmCloseDialogOpen}
            prompt={
              currentStep === steps.enterEmail
                ? "There is unsaved data present, are you sure you want to close?"
                : "The change email process is incomplete are you sure you want to abandon this?"
            }
            confirmBtnLabel={
              currentStep === steps.enterEmail ? "Close" : "Abandon"
            }
            onConfirm={onConfirmCloseDialogAccept}
            onCancel={onConfirmCloseDialogCancel}
          />
        </>
      )}
    </>
  );
};

export default ChangeEmailDialog;
